import { useState, useEffect, useCallback } from "react";
import { fetchSoa, downloadPdf } from "../../apiService";

const fmt = new Intl.NumberFormat("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtAed = v => `AED ${fmt.format(v)}`;
const pct = (part, total) => total > 0 ? ((part / total) * 100).toFixed(1) : "0.0";

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10, padding: "16px 20px",
      boxShadow: "0 1px 4px rgba(0,0,0,.08)", borderLeft: `4px solid ${accent}`,
      minWidth: 180, flex: 1,
    }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#111" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function AgingBar({ aging, total }) {
  if (!aging || total <= 0) return null;
  const segments = [
    { key: "current",    label: "Current",  color: "#22c55e" },
    { key: "days0to30",  label: "0–30d",    color: "#f59e0b" },
    { key: "days31to60", label: "31–60d",   color: "#f97316" },
    { key: "days61to90", label: "61–90d",   color: "#ef4444" },
    { key: "over90",     label: ">90d",     color: "#7f1d1d" },
  ];
  return (
    <div>
      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginBottom: 6 }}>
        {segments.map(s => {
          const w = pct(aging[s.key] || 0, total);
          return parseFloat(w) > 0 ? (
            <div key={s.key} style={{ width: `${w}%`, background: s.color }} title={`${s.label}: ${fmtAed(aging[s.key] || 0)}`} />
          ) : null;
        })}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {segments.map(s => {
          const v = aging[s.key] || 0;
          if (v <= 0) return null;
          return (
            <span key={s.key} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, display: "inline-block" }} />
              {s.label}: <b>{fmtAed(v)}</b>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function OverdueBadge({ days }) {
  if (!days || days <= 0) return <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600 }}>Current</span>;
  const color = days > 90 ? "#7f1d1d" : days > 60 ? "#ef4444" : days > 30 ? "#f97316" : "#f59e0b";
  return (
    <span style={{ fontSize: 11, background: color, color: "#fff", borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>
      {days}d overdue
    </span>
  );
}

function BillsModal({ customer, spName, businessUnit, onClose }) {
  return (
    <div className="overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 820, width: "95vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{customer.accountName}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{spName} · {businessUnit}</div>
          </div>
          <button className="btn" onClick={onClose}>✕ Close</button>
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 16px", flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Total Outstanding</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtAed(customer.totalOutstanding)}</div>
          </div>
          <div style={{ background: customer.overdueAmount > 0 ? "#fef2f2" : "#f0fdf4", borderRadius: 8, padding: "10px 16px", flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Overdue</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: customer.overdueAmount > 0 ? "#dc2626" : "#16a34a" }}>{fmtAed(customer.overdueAmount)}</div>
          </div>
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 16px", flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Credit Terms</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{customer.creditTerms || "CDC/Cash"}</div>
          </div>
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 16px", flex: 1 }}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>Credit Limit</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: customer.creditLimit ? "#111" : "#9ca3af" }}>
              {customer.creditLimit ? fmtAed(customer.creditLimit) : "—"}
            </div>
          </div>
          {customer.hasPdc && (
            <div style={{ background: "#eff6ff", borderRadius: 8, padding: "10px 16px", flex: 1 }}>
              <div style={{ fontSize: 11, color: "#6b7280" }}>PDC Issued</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#2563eb" }}>Yes</div>
            </div>
          )}
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Invoice</th>
                <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Bill Date</th>
                <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Due Date</th>
                <th style={{ padding: "8px 12px", textAlign: "right", borderBottom: "1px solid #e5e7eb" }}>Amount (AED)</th>
                <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(customer.bills || []).map((bill, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: bill.overdue ? "#fffbeb" : "white" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 600 }}>
                    {bill.invoiceRef}
                    {bill.hasPdc && <span style={{ marginLeft: 6, fontSize: 10, background: "#dbeafe", color: "#1d4ed8", borderRadius: 4, padding: "2px 5px" }}>PDC</span>}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#6b7280" }}>{bill.billDate}</td>
                  <td style={{ padding: "8px 12px", color: "#6b7280" }}>{bill.dueDate}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{fmt.format(bill.pendingAmount)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                    <OverdueBadge days={bill.daysOverdue} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f9fafb", fontWeight: 700 }}>
                <td colSpan={3} style={{ padding: "8px 12px" }}>Total</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt.format(customer.totalOutstanding)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: "7px 10px", textAlign: "left", borderBottom: "1px solid #e5e7eb", fontWeight: 600, fontSize: 12, color: "#374151" };

function CompanyBlock({ co, spName, onViewBills }) {
  const [downloadingId, setDownloadingId] = useState(null);

  function handlePdf(cust, companyId) {
    if (downloadingId) return;
    setDownloadingId(cust.accountId);
    downloadPdf(
      `/api/crm/soa/customer/${cust.accountId}/pdf?companyId=${companyId}`,
      `SOA-${cust.accountName.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`
    ).finally(() => setDownloadingId(null));
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid #e5e7eb",
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1d4ed8" }}>{co.businessUnit}</span>
        <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: 12 }}>
          {co.overdueAmount > 0 && (
            <span style={{ color: "#dc2626", fontWeight: 600 }}>⚠ {fmtAed(co.overdueAmount)} overdue</span>
          )}
          <span style={{ fontWeight: 700, color: "#111" }}>{fmtAed(co.totalOutstanding)}</span>
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <AgingBar aging={co.aging} total={co.totalOutstanding} />
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            <th style={thStyle}>Customer</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Outstanding</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Overdue</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Bills</th>
            <th style={{ ...thStyle, textAlign: "center" }}>PDC</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Terms</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Credit Limit</th>
            <th style={{ ...thStyle }} />
          </tr>
        </thead>
        <tbody>
          {(co.customers || []).map((cust, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: cust.overdueAmount > 0 ? "#fffbeb" : "white" }}>
              <td style={{ padding: "7px 10px", fontWeight: 500 }}>{cust.accountName}</td>
              <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmtAed(cust.totalOutstanding)}</td>
              <td style={{ padding: "7px 10px", textAlign: "right", color: cust.overdueAmount > 0 ? "#dc2626" : "#6b7280", fontWeight: cust.overdueAmount > 0 ? 600 : 400 }}>
                {cust.overdueAmount > 0 ? fmtAed(cust.overdueAmount) : "—"}
              </td>
              <td style={{ padding: "7px 10px", textAlign: "center", color: "#6b7280" }}>{cust.bills?.length || 0}</td>
              <td style={{ padding: "7px 10px", textAlign: "center" }}>
                {cust.hasPdc ? <span style={{ color: "#2563eb", fontSize: 11, fontWeight: 600 }}>PDC</span> : <span style={{ color: "#d1d5db" }}>—</span>}
              </td>
              <td style={{ padding: "7px 10px", textAlign: "center", fontSize: 12, fontWeight: 600,
                color: cust.creditTerms === "CDC/Cash" ? "#6b7280" : "#111" }}>
                {cust.creditTerms || "CDC/Cash"}
              </td>
              <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12,
                color: cust.creditLimit ? "#111" : "#d1d5db" }}>
                {cust.creditLimit ? fmtAed(cust.creditLimit) : "—"}
              </td>
              <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }}
                  onClick={() => onViewBills(cust, co.businessUnit)}>
                  View Bills
                </button>
                <button className="btn"
                  style={{ fontSize: 11, padding: "3px 10px", marginLeft: 6,
                    opacity: downloadingId === cust.accountId ? 0.6 : 1,
                    cursor: downloadingId ? "not-allowed" : "pointer" }}
                  disabled={!!downloadingId}
                  onClick={() => handlePdf(cust, co.companyId)}>
                  {downloadingId === cust.accountId ? "Downloading…" : "PDF"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SalespersonSection({ sp, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [modal, setModal] = useState(null); // { customer, businessUnit }

  const totalCustomers = (sp.companies || []).reduce((sum, co) => sum + (co.customers?.length || 0), 0);
  const overdueRatio = sp.totalOutstanding > 0 ? pct(sp.overdueAmount, sp.totalOutstanding) : "0.0";

  return (
    <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,.08)", marginBottom: 12, overflow: "hidden" }}>
      <div
        style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, borderBottom: open ? "1px solid #f3f4f6" : "none" }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ fontSize: 14, color: "#6b7280" }}>{open ? "▾" : "▸"}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{sp.name}</span>
          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 10 }}>{totalCustomers} customers</span>
        </div>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {sp.overdueAmount > 0 && (
            <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
              ⚠ {fmtAed(sp.overdueAmount)} overdue ({overdueRatio}%)
            </span>
          )}
          <span style={{ fontWeight: 700, fontSize: 14 }}>{fmtAed(sp.totalOutstanding)}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: "14px 18px" }}>
          <div style={{ marginBottom: 16 }}>
            <AgingBar aging={sp.aging} total={sp.totalOutstanding} />
          </div>
          {(sp.companies || []).map(co => (
            <CompanyBlock
              key={co.companyId}
              co={co}
              spName={sp.name}
              onViewBills={(cust, bu) => setModal({ customer: cust, businessUnit: bu })}
            />
          ))}
        </div>
      )}

      {modal && (
        <BillsModal
          customer={modal.customer}
          spName={sp.name}
          businessUnit={modal.businessUnit}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

export function PageSOAPaymentFollowUp({ myPerms = [], userEmail = "" }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState("");
  const [spFilter, setSpFilter] = useState("ALL");
  const [showUnmatched, setShowUnmatched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchSoa()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading Bills Receivable from Tally…</div>;
  if (error)   return (
    <div style={{ padding: 24 }}>
      <div className="alert-warn" style={{ marginBottom: 12 }}>Error: {error}</div>
      <button className="btn btn-primary" onClick={load}>Retry</button>
    </div>
  );
  if (!data)   return null;

  const { summary, salespersons = [], unmatched = [], fetchedAt } = data;

  const canManage = myPerms.includes("CRM_MANAGE");

  const spNames = ["ALL", ...salespersons.map(s => s.name)];

  const filteredSps = salespersons.filter(sp => {
    if (spFilter !== "ALL" && sp.name !== spFilter) return false;
    if (search) return sp.companies?.some(co =>
      co.customers?.some(c => c.accountName?.toLowerCase().includes(search.toLowerCase())));
    return true;
  }).map(sp => {
    if (!search) return sp;
    return {
      ...sp,
      companies: sp.companies?.map(co => ({
        ...co,
        customers: co.customers?.filter(c =>
          c.accountName?.toLowerCase().includes(search.toLowerCase())),
      })).filter(co => co.customers?.length > 0),
    };
  });

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>SOA & Payment Follow-up</h2>
          {fetchedAt && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              {fetchedAt.includes("Cache") ? fetchedAt : `Tally data as of: ${new Date(fetchedAt).toLocaleString("en-AE")} (refreshes every 6 hours)`}
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={load} style={{ fontSize: 12 }}>↺ Refresh</button>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <SummaryCard label="Total Outstanding" value={fmtAed(summary.totalOutstanding)} sub={`${summary.totalBills} invoices`} accent="#3b82f6" />
        <SummaryCard label="Overdue" value={fmtAed(summary.overdueAmount)} sub={`${summary.overdueBills} invoices`} accent="#ef4444" />
        <SummaryCard label="Current (Not Yet Due)" value={fmtAed(summary.currentAmount)} accent="#22c55e" />
        {canManage && summary.unmatchedBills > 0 && (
          <SummaryCard label="Unmatched Bills" value={summary.unmatchedBills} sub="Not linked to CRM accounts" accent="#f59e0b" />
        )}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          className="input"
          placeholder="Search customer..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 220, fontSize: 13 }}
        />
        {spNames.length > 2 && (
          <select className="input" value={spFilter} onChange={e => setSpFilter(e.target.value)} style={{ maxWidth: 200, fontSize: 13 }}>
            {spNames.map(n => <option key={n} value={n}>{n === "ALL" ? "All Salespersons" : n}</option>)}
          </select>
        )}
        <span style={{ fontSize: 12, color: "#6b7280" }}>{filteredSps.length} salesperson{filteredSps.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Salesperson sections */}
      {filteredSps.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>No results found.</div>
      ) : (
        filteredSps.map((sp, i) => (
          <SalespersonSection key={sp.name} sp={sp} defaultOpen={i === 0 && filteredSps.length === 1} />
        ))
      )}

      {/* Unmatched bills (admin only) */}
      {canManage && unmatched.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button className="btn" onClick={() => setShowUnmatched(o => !o)} style={{ fontSize: 12, marginBottom: 8 }}>
            {showUnmatched ? "▾" : "▸"} Unmatched Bills ({unmatched.length}) — not linked to CRM accounts
          </button>
          {showUnmatched && (
            <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,.08)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#fef3c7" }}>
                    <th style={{ padding: "7px 12px", textAlign: "left", borderBottom: "1px solid #fde68a" }}>Invoice</th>
                    <th style={{ padding: "7px 12px", textAlign: "left", borderBottom: "1px solid #fde68a" }}>Party (Tally)</th>
                    <th style={{ padding: "7px 12px", textAlign: "left", borderBottom: "1px solid #fde68a" }}>Company</th>
                    <th style={{ padding: "7px 12px", textAlign: "left", borderBottom: "1px solid #fde68a" }}>Due Date</th>
                    <th style={{ padding: "7px 12px", textAlign: "right", borderBottom: "1px solid #fde68a" }}>Amount (AED)</th>
                    <th style={{ padding: "7px 12px", textAlign: "center", borderBottom: "1px solid #fde68a" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.map((b, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 12px", fontWeight: 500 }}>{b.invoiceRef}</td>
                      <td style={{ padding: "6px 12px", color: "#6b7280" }}>{b.billParty}</td>
                      <td style={{ padding: "6px 12px", color: "#6b7280" }}>{b.businessUnit}</td>
                      <td style={{ padding: "6px 12px", color: "#6b7280" }}>{b.dueDate}</td>
                      <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600 }}>{fmt.format(b.pendingAmount)}</td>
                      <td style={{ padding: "6px 12px", textAlign: "center" }}><OverdueBadge days={b.daysOverdue} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
